import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Modal, Button, Form, Row, Col, Container, Card, Table } from "react-bootstrap";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import "./../../assets/scss/reports/cashbook.scss";
import PageHeader from "../PageHeader";
import { DeleteIcon, EditIcon, MoneyDollarIcon, PDFIcon } from "../Icons";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import CurrencyInput from "react-currency-input-field";
import currency from "currency.js";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { isEmpty, pickBy } from "lodash";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import { BudgetDetailsModal, BudgetSetupModal } from "./Budget/BudgetModal";
import { formatDate } from "../../utils/helpers";
import ConfirmDialog from "../ConfirmDialogue";

// Options for react-select
const periodOptions = [
  { value: "FY 2024 (Jan 2024 - Dec 2024)", label: "FY 2024 (Jan 2024 - Dec 2024)" },
  { value: "FY 2025 (Jan 2025 - Dec 2025)", label: "FY 2025 (Jan 2025 - Dec 2025)" },
  { value: "FY 2026 (Jan 2026 - Dec 2026)", label: "FY 2026 (Jan 2026 - Dec 2026)" },
  { value: "FY 2027 (Jan 2027 - Dec 2027)", label: "FY 2027 (Jan 2027 - Dec 2027)" },
  { value: "FY 2028 (Jan 2028 - Dec 2028)", label: "FY 2028 (Jan 2028 - Dec 2028)" },
];

const profitLossOptions = [
  { value: "", label: "Optional Select..." },
  { value: "last_year", label: "Last Year Data" },
  { value: "custom", label: "Custom Data" },
];

// Validation schemas
const budgetSetupSchema = Yup.object().shape({
  budgetType: Yup.string().required("Budget type is required"),
  period: Yup.string().required("Period is required"),
  budgetFormat: Yup.string().required("Budget format is required"),
  profitLoss: Yup.string(),
  availableSetupOption: Yup.string().required("Setup option is required"),
});

const budgetDetailSchema = Yup.object().shape({
  accounts: Yup.array().min(1, "Please select at least one account"),
});

// API service functions
const budgetAPI = {
  // Fetch all budgets
  getBudgets: async backendUrl => {
    const response = await fetch(`${backendUrl}/api/budget`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch budgets");
    }

    return response.json();
  },

  // Fetch budget accounts
  getBudgetAccounts: async backendUrl => {
    const response = await fetch(`${backendUrl}/api/budget/accounts`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch budget accounts");
    }

    return response.json();
  },

  // Create new budget
  createBudget: async (backendUrl, budgetData) => {
    const response = await fetch(`${backendUrl}/api/budget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(budgetData),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to create budget");
    }

    return response.json();
  },

  // Update budget
  updateBudget: async (backendUrl, budgetId, budgetData) => {
    const response = await fetch(`${backendUrl}/api/budget/${budgetId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(budgetData),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to update budget");
    }

    return response.json();
  },

  // Delete budget
  deleteBudget: async (backendUrl, budgetId) => {
    const response = await fetch(`${backendUrl}/api/budget/${budgetId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to delete budget");
    }

    return response.json();
  },

  // Get single budget
  getBudget: async (backendUrl, budgetId) => {
    const response = await fetch(`${backendUrl}/api/budget/${budgetId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch budget");
    }

    return response.json();
  },
};

const Budget = () => {
  const { backendUrl } = useAuth();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showBudgetModal, setBudgetModal] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const queryClient = useQueryClient();

  const [isOpen, setOpen] = useState({});

  // Fetch accounts using React Query
  const {
    data: { budgetAccounts: accounts } = { budgetAccounts: [] },
    isLoading: accountsLoading,
    error: accountsError,
  } = useQuery("budget_accounts", () => budgetAPI.getBudgetAccounts(backendUrl), {
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: error => {
      console.error("Failed to fetch accounts:", error);
      toast.error("Failed to load accounts. Using fallback data.");
    },
  });

  // Fetch budgets using React Query
  const {
    data: { budgets } = { budgets: [] },
    isLoading: budgetsLoading,
    error: budgetsError,
  } = useQuery("budgets", () => budgetAPI.getBudgets(backendUrl), {
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: error => {
      console.error("Failed to fetch budgets:", error);
      toast.error("Failed to load budgets");
    },
  });

  // Create budget mutation
  const createBudgetMutation = useMutation(
    budgetData => budgetAPI.createBudget(backendUrl, budgetData),
    {
      onSuccess: data => {
        queryClient.invalidateQueries("budgets");
        setBudgetModal(false);
        setShowSetupModal(false);
        setSetupData(null);
        setEditingBudget(null);
        setupFormik.resetForm({
          budgetType: "profit_and_loss",
          period: "FY 2025 (Jan 2025 - Dec 2025)",
          budgetFormat: "consolidated",
          profitLoss: "",
          availableSetupOption: "custom",
        });

        // RETAIN ACCOUNTS
        budgetFormik.resetForm({ accounts: budgetFormik.values.accounts });

        toast.success("Budget created successfully!");
      },
      onError: error => {
        console.error("Failed to create budget:", error);
        toast.error("Failed to create budget. Please try again.");
      },
    }
  );

  // Update budget mutation
  const updateBudgetMutation = useMutation(
    ({ budgetId, budgetData }) => budgetAPI.updateBudget(backendUrl, budgetId, budgetData),
    {
      onSuccess: data => {
        queryClient.invalidateQueries("budgets");
        setBudgetModal(false);
        setShowSetupModal(false);
        setSetupData(null);
        setEditingBudget(null);
        setupFormik.resetForm();

        // RETAIN ACCOUNTS
        budgetFormik.resetForm({ accounts: budgetFormik.values.accounts });

        toast.success("Budget updated successfully!");
      },
      onError: error => {
        console.error("Failed to update budget:", error);
        toast.error("Failed to update budget. Please try again.");
      },
    }
  );

  // Delete budget mutation
  const deleteBudgetMutation = useMutation(
    budgetId => budgetAPI.deleteBudget(backendUrl, budgetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("budgets");
        toast.success("Budget deleted successfully!");
      },
      onError: error => {
        console.error("Failed to delete budget:", error);
        toast.error("Failed to delete budget. Please try again.");
      },
    }
  );

  // Delete budget function
  const deleteBudget = async budgetId => {
    if (
      await ConfirmDialog({
        title: "Are you sure you want to delete this budget?",
        type: "danger",
      })
    ) {
      deleteBudgetMutation.mutate(budgetId);
    }
  };

  // Edit budget function
  const editBudget = async budget => {
    try {
      // Fetch full budget details if needed
      // const fullBudget = await budgetAPI.getBudget(backendUrl, budget.id);

      setEditingBudget(budget);

      // Populate setup form
      setupFormik.setValues({
        budgetType: budget.budgetType,
        period: budget.period,
        budgetFormat: budget.budgetFormat,
        profitLoss: budget.profitLoss || "",
        availableSetupOption: budget.availableSetupOption || "custom",
      });

      // Populate budget form
      budgetFormik.setValues({
        accounts: budget.accounts || [],
        budgetTotals: budget.budgetTotals || {},
        monthlyBudgets: budget.monthlyBudgets || {},
      });

      setSetupData(budget);
      setBudgetModal(true);
    } catch (error) {
      console.error("Failed to fetch budget details:", error);
      toast.error("Failed to load budget details");
    }
  };

  // Print function (unchanged)
  const handlePrint = (budget = null) => {
    const printContent = generateBudgetPrintContent(budget);

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Budget Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .header { text-align: center; margin-bottom: 30px; }
            .budget-section { margin-bottom: 40px; page-break-inside: avoid; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const generateBudgetPrintContent = budget => {
    const getAccountNameById = id => {
      const findAccount = accounts => {
        for (const account of accounts) {
          if (account.AccountID === id) return account;
          if (account.children) {
            const found = findAccount(account.children);
            if (found) return found;
          }
        }
        return null;
      };
      return findAccount(accounts)?.name || `Account ${id}`;
    };

    const months = [
      "Jan 2025",
      "Feb 2025",
      "Mar 2025",
      "Apr 2025",
      "May 2025",
      "Jun 2025",
      "Jul 2025",
      "Aug 2025",
      "Sep 2025",
      "Oct 2025",
      "Nov 2025",
      "Dec 2025",
    ];

    return `
      <div class="header">
        <h1>Budget Report</h1>
        <h2>${budget?.budgetType?.replace("_", " ").toUpperCase()}</h2>
        <p><strong>Period:</strong> ${budget.period}</p>
        <p><strong>Format:</strong> ${budget.budgetFormat}</p>
        <p><strong>Created:</strong> ${new Date(budget.createdAt).toLocaleDateString()}</p>
      </div>
      <div class="budget-section">
        <table>
          <thead>
            <tr>
              <th>Accounts</th>
              <th>Budget Totals</th>
              ${months.map(month => `<th>${month}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${budget.accounts
              ?.map(
                accountId => `
              <tr>
                <td>${getAccountNameById(accountId)}</td>
                <td>${budget.budgetTotals?.[accountId] || "0.00"}</td>
                ${months
                  .map(
                    (_, index) =>
                      `<td>${
                        budget.monthlyBudgets?.[`${accountId}_month_${index + 1}`]
                          ? currency(budget.monthlyBudgets?.[`${accountId}_month_${index + 1}`], {
                              symbol: "",
                            }).format()
                          : ""
                      }</td>`
                  )
                  .join("")}
              </tr>
            `
              )
              .join("") || '<tr><td colspan="14">No accounts selected</td></tr>'}
          </tbody>
        </table>
      </div>
    `;
  };

  // Setup form using useFormik
  const setupFormik = useFormik({
    initialValues: {
      budgetType: "profit_and_loss",
      period: "FY 2025 (Jan 2025 - Dec 2025)",
      budgetFormat: "consolidated",
      profitLoss: "",
      availableSetupOption: "custom",
    },
    validationSchema: budgetSetupSchema,
    onSubmit: values => {
      setSetupData(values);
      setShowSetupModal(false);
      setBudgetModal(true);
    },
  });

  // Helper function to get all account IDs including nested ones
  const getAllAccountIds = accounts => {
    const ids = [];
    const traverse = accountList => {
      accountList.forEach(account => {
        if (!account.isHeader) ids.push(account.id);
        if (account.children && account.children.length > 0) {
          traverse(account.children);
        }
      });
    };
    traverse(accounts);
    return ids;
  };

  // Budget details form using useFormik
  const budgetFormik = useFormik({
    initialValues: useMemo(
      () => ({
        accounts: accounts ? getAllAccountIds(accounts) : [],
        budgetTotals: {},
        monthlyBudgets: {},
      }),
      [accounts]
    ),
    validationSchema: budgetDetailSchema,
    onSubmit: values => {
      const budgetData = {
        ...setupData,
        ...values,
      };

      if (editingBudget) {
        updateBudgetMutation.mutate({ budgetId: editingBudget.id, budgetData });
      } else {
        createBudgetMutation.mutate(budgetData);
      }
    },
  });

  const handleBudgetChange = (field, value) => {
    budgetFormik.setFieldValue(field, value);
  };

  const renderNestedAccounts = (accounts, level = 0) => {
    return accounts.map(account => (
      <React.Fragment key={account.AccountID}>
        {account.isHeader ? (
          <tr>
            <td colSpan={16} className="align-middle">
              <p className="p-0">
                <Button
                  variant=""
                  onClick={() => {
                    setOpen(oldData => ({
                      ...oldData,
                      [account.name]:
                        typeof oldData[account.name] === "undefined"
                          ? false
                          : !oldData[account.name],
                    }));
                  }}
                  className="p-3 py-2 d-flex align-items-center ps-0"
                >
                  <ChevronDownIcon
                    style={{
                      ...(typeof isOpen[account.name] === "undefined" || isOpen[account.name]
                        ? {}
                        : { transform: "rotate(-180deg)" }),
                      transition: "transform 0.3s ease",
                    }}
                  />
                  <b>{account.name}</b>
                </Button>{" "}
              </p>
            </td>
          </tr>
        ) : typeof isOpen[account.name] === "undefined" || isOpen[account.name] ? (
          <tr>
            <td className="align-middle">
              <div
                style={{
                  paddingLeft: `${level * 20}px`,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <div title={account.name} className="text-truncate">
                  {account.name}
                </div>
              </div>
            </td>

            <td title="Actuals 2025 (YTD)" className="align-middle">
              <span>{account?.actualYTD || "0.00"}</span>
            </td>

            <td className="align-middle">
              {(() => {
                const total = Array.from({ length: 12 }, (_, i) => {
                  const value =
                    budgetFormik.values.monthlyBudgets[`${account.AccountID}_month_${i + 1}`];
                  return parseFloat(value) || 0;
                }).reduce((sum, value) => sum + value, 0);

                return currency(total, {
                  symbol: "",
                }).format();
              })()}
            </td>

            {Array.from({ length: 12 }, (_, i) => (
              <td key={i}>
                <div style={{ width: "100px" }}>
                  <CurrencyInput
                    placeholder="0.00"
                    className="form-control"
                    decimalsLimit={2}
                    value={
                      budgetFormik.values.monthlyBudgets[`${account.AccountID}_month_${i + 1}`] ||
                      ""
                    }
                    onValueChange={value =>
                      handleBudgetChange(
                        `monthlyBudgets.${account.AccountID}_month_${i + 1}`,
                        value
                      )
                    }
                  />
                </div>
              </td>
            ))}
          </tr>
        ) : null}

        {typeof isOpen[account.name] === "undefined" || isOpen[account.name] ? (
          <>
            {account.children && renderNestedAccounts(account.children, level + 1)}

            {/* TOTALS */}
            {account.isHeader && (
              <tr style={{ backgroundColor: "#f8f9fa", pointerEvents: "none" }}>
                <td className="align-middle">
                  <div
                    style={{
                      paddingLeft: `${level * 20}px`,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <div title={account.name} className="text-truncate ms-4">
                      <b>Total {account.name}</b>
                    </div>
                  </div>
                </td>

                <td title="Actuals 2025 (YTD)" className="align-middle">
                  <span>{account?.actualYTD || "0.00"}</span>
                </td>

                <td className="align-middle">
                  {(() => {
                    const filtered = pickBy(budgetFormik.values.monthlyBudgets, (value, key) =>
                      account.children.some(childAccount =>
                        key.startsWith(`${childAccount.AccountID}_month`)
                      )
                    );

                    return currency(
                      Object.values(filtered).reduce((acc, curr) => acc + parseFloat(curr) || 0, 0),
                      {
                        symbol: "",
                      }
                    ).format();
                  })()}
                </td>

                {Array.from({ length: 12 }, (_, i) => (
                  <td key={i}>
                    <div style={{ width: "100px" }} className="p-2">
                      {(() => {
                        const total = account.children
                          .map(childAccount => {
                            return (
                              budgetFormik.values.monthlyBudgets[
                                `${childAccount.AccountID}_month_${i + 1}`
                              ] || 0
                            );
                          })
                          .reduce((acc, curr) => acc + parseFloat(curr), 0);

                        return currency(total, {
                          symbol: "",
                        }).format();
                      })()}
                    </div>
                  </td>
                ))}
              </tr>
            )}
          </>
        ) : null}
      </React.Fragment>
    ));
  };

  const handleEdit = budget => {
    editBudget(budget);
  };

  return (
    <>
      <main className="inner-page-outlet">
        <PageHeader
          name="Budget Management"
          description="Manage your budgets effectively"
          icon={<MoneyDollarIcon />}
        />
        <main className="cash-book">
          <div className="content p-3">
            <div />
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <Button variant="primary" onClick={() => setShowSetupModal(true)}>
                  + Add New Budget
                </Button>
              </div>
            </div>

            {budgetsLoading ? (
              <div className="text-center">Loading budgets...</div>
            ) : budgetsError ? (
              <div className="alert alert-danger">Failed to load budgets</div>
            ) : budgets.length === 0 ? (
              <p>No budgets created yet.</p>
            ) : (
              <div className="table-responsive">
                <Table hover className="product-table">
                  <thead>
                    <tr>
                      <th>Budget Type</th>
                      <th>Period</th>
                      <th>Format</th>
                      {/* <th>Accounts</th> */}
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(budgets) &&
                      budgets?.map(budget => (
                        <tr key={budget.id}>
                          <td>{budget.budgetType?.replace("_", " ").toUpperCase()}</td>
                          <td>{budget.period}</td>
                          <td>{budget.budgetFormat}</td>
                          {/* <td>{budget.accounts?.length || 0}</td> */}
                          <td>{formatDate(budget.createdAt)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                handleEdit(budget);
                              }}
                            >
                              View / Edit
                            </Button>

                            <Button
                              variant="light-blue"
                              className="border me-2"
                              size="sm"
                              onClick={() => handlePrint(budget)}
                            >
                              Print
                            </Button>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => deleteBudget(budget.id)}
                              disabled={deleteBudgetMutation.isLoading}
                            >
                              {deleteBudgetMutation.isLoading ? "Deleting..." : "Delete"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </main>
      </main>

      {/* Budget Details Modal */}
      {showBudgetModal && (
        <BudgetDetailsModal
          showBudgetModal={showBudgetModal}
          setBudgetModal={setBudgetModal}
          setEditingBudget={setEditingBudget}
          editingBudget={editingBudget}
          setupData={setupData}
          budgetFormik={budgetFormik}
          accountsLoading={accountsLoading}
          accounts={accounts}
          renderNestedAccounts={renderNestedAccounts}
          createBudgetMutation={createBudgetMutation}
          updateBudgetMutation={updateBudgetMutation}
          setShowSetupModal={setShowSetupModal}
        />
      )}

      {/* Budget Setup Modal */}
      {showSetupModal && (
        <BudgetSetupModal
          setupFormik={setupFormik}
          showSetupModal={showSetupModal}
          setShowSetupModal={setShowSetupModal}
          editingBudget={editingBudget}
          periodOptions={periodOptions}
          profitLossOptions={profitLossOptions}
        />
      )}
    </>
  );
};

export default Budget;
